import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { formatDate } from '@/core/helpers/DateTime';
import { DataSource } from '@/types';
import {
         TableColumn,
         TableComponent,
         TableComponentProps,
         columnTemplates
        } from '@/core/components/table';
import { PlannedOperation } from '@/dto';
import { Consist, Icon } from '@/core/components/controls';

@Component
export default class PlannedOperationsTable extends Vue {
    @Prop({ type: String, required: true }) private tableKey!: string;
    @Prop({ type: Object, required: true }) private dataSource!: DataSource;
    @Prop({ type: Array, required: false, default: [] }) private vehicles!: any[];

    public resized() {
        const table: any = this.$refs['table-control'];

        if (table && table.resized) {
            table.resized();
        }
    }

    public render(createElement: Vue.CreateElement): Vue.VNode {
        const { $listeners, tableKey, dataSource, vehicles } = this;
        const columns = this.columns(vehicles);

        return createElement(TableComponent, {
            props: {
                tableKey,
                dataSource,
                columns,
            } as TableComponentProps,
            on: {
                ...$listeners,
            },
            ref: 'table-control'
        });
    }

    private columns(vehicles: any[]): TableColumn[] {
        return [
            {
                dataField: 'mainResource.name',
                caption: 'GridColumnTitleObject',
            }, {
                dataField: 'resourceNames',
                caption: 'GridColumnTitleResourceNames',
                ...columnTemplates.ARRAY('resourceIds', 'id', 'name', this.vehicles),
            }, {
                dataField: 'startSite.name',
                caption: 'GridColumnTitleStartSite',
            }, {
                dataField: 'finishSite.name',
                caption: 'GridColumnTitleFinishSite',
            }, {
                dataField: 'startTime',
                caption: 'GridColumnTitleStartTime',
                ...columnTemplates.DATE_WITH_TIME()
            }, {
                dataField: 'finishTime',
                caption: 'GridColumnTitleFinishTime',
                ...columnTemplates.DATE_WITH_TIME()
            }, {
                dataField: 'type',
                caption: 'GridColumnTitleType',
                ...columnTemplates.REPLACE_VALUE_WITH_LOCALIZED('EventType', 'type')
            }, {
                dataField: 'linkedResource.name',
                caption: 'GridColumnTitleLinkedObject',
            }, {
                dataField: 'mainResourceType.name',
                caption: 'GridColumnTitleMainType',
            }, {
                name: 'mainResourceMaxWeightKilos',
                dataField: 'mainResource.maxWeight',
                caption: 'GridColumnTitleMainWeight',
                ...columnTemplates.WEIGHT(),
            }, {
                name: 'mainResourceMaxWeightTonns',
                dataField: 'mainResource.maxWeight',
                caption: 'GridColumnTitleMainWeightTonns',
                ...columnTemplates.WEIGHT(),
                calculateCellValue: (rowData) => {
                    if (rowData.mainResource && rowData.mainResource.maxWeight) {
                        return rowData.mainResource.maxWeight / 1000;
                    }
                },
                ...columnTemplates.WEIGHT(),
            }, {
                dataField: 'mainResource.volume',
                caption: 'GridColumnTitleMainVolume',
                ...columnTemplates.VOLUME(),
            }, {
                dataField: 'mainResource.maxArea',
                caption: 'GridColumnTitleMainArea',
                ...columnTemplates.AREA(),
            }, {
                dataField: 'linkedResourceType.name',
                caption: 'GridColumnTitleLinkedType',
            }, {
                name: 'linkedResourceMaxWeightKilos',
                dataField: 'linkedResource.maxWeight',
                caption: 'GridColumnTitleLinkedWeight',
                ...columnTemplates.WEIGHT(),
            }, {
                name: 'linkedResourceMaxWeightTonns',
                dataField: 'linkedResource.maxWeight',
                caption: 'GridColumnTitleLinkedWeightTonns',
                ...columnTemplates.WEIGHT(),
                calculateCellValue: (rowData) => {
                    if (rowData.linkedResource && rowData.linkedResource.maxWeight) {
                        return rowData.linkedResource.maxWeight / 1000;
                    }
                },
                ...columnTemplates.WEIGHT(),
            }, {
                dataField: 'linkedResource.volume',
                caption: 'GridColumnTitleLinkedVolume',
                ...columnTemplates.VOLUME(),
            }, {
                dataField: 'linkedResource.maxArea',
                caption: 'GridColumnTitleLinkedArea',
                ...columnTemplates.AREA(),
            }, {
                dataField: 'timeDuration',
                caption: 'GridColumnTitleDuration',
            }, {
                dataField: 'packs',
                caption: 'GridColumnTitleConsist',
                ...columnTemplates.COMPONENT<PlannedOperation>(
                    (rowData) => {
                        if (rowData.packs && rowData.packs > 0) {
                            return new Consist({
                                propsData: {
                                    title: 'GridColumnTitleConsist',
                                    flow: false,
                                    operation: rowData
                                },
                            });
                        }
                    }
                ),
            }, {
                dataField: 'flow',
                caption: 'GridColumnTitleOperationFlow',
                ...columnTemplates.COMPONENT<PlannedOperation>(
                    (rowData) => {
                        if (rowData.flow && rowData.flow > 0) {
                            return new Consist({
                                propsData: {
                                    title: 'GridColumnTitleOperationFlow',
                                    operation: rowData,
                                    flow: true,
                                    new: true
                                },
                            });
                        }
                    }
                ),
            }, {
                dataField: 'volume',
                caption: 'GridColumnTitleSumVolume',
                ...columnTemplates.VOLUME(),
            }, {
                name: 'weightKilos',
                dataField: 'weight',
                caption: 'GridColumnTitleSumWeight',
                ...columnTemplates.WEIGHT(),
            }, {
                name: 'weightTonns',
                dataField: 'weight',
                caption: 'GridColumnTitleSumWeightTonns',
                ...columnTemplates.WEIGHT(),
                calculateCellValue: (rowData) => {
                    if (rowData.weight && rowData.weight) {
                        return rowData.weight / 1000;
                    }
                },
            }, {
                dataField: 'area',
                caption: 'GridColumnTitleSumArea',
                ...columnTemplates.AREA(),
            }, {
                dataField: 'productTypes',
                caption: 'GridColumnTitleProductType',
            }, {
                dataField: 'areaPlaces',
                caption: 'GridColumnTitleAreaPlaces',
            }, {
                dataField: 'assembleInAdvance',
                caption: 'GridColumnTitleAssembleInAdvance',
                ...columnTemplates.COMPONENT<PlannedOperation>(
                    (rowData) => {
                        if (rowData.assembleInAdvance) {
                            return new Icon({
                                propsData: {
                                    name: 'drawer'
                                },
                            });
                        }
                    }
                ),
            }
        ];
    }
}
